const buildAttendanceDisplayText = (displayData) => {
  if (!displayData) return "";
  const status = (displayData.status || "").toString().trim().toLowerCase();

  const map = {
    present: "Present",
    late: "Late",
    absent: "Absent",
    "half-day": "Half Day",
    halfday: "Half Day",
    leave: "Leave",
    "on-leave": "Leave",
    "week-off": "Week Off",
    weekoff: "Week Off",
    holiday: "Holiday",
  };

  return map[status] || displayData.status || "";
};

const formatPdfTime = (value) => {
  if (!value) return "--";
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    // Punch times are stored as UTC-field timestamps (the app reads them with
    // getUTCHours elsewhere, e.g. formatTime). Format in UTC so the PDF matches
    // the on-screen values instead of re-applying the device's local offset.
    return parsed.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "UTC",
    });
  }
  return typeof value === "string" && value.length >= 5 ? value.slice(0, 5) : String(value);
};

const getPdfCalendarCellStyle = (status) => {
  const styles = {
    present: { fillColor: "#F0FDF4", textColor: "#166534", borderColor: "#DCFCE7" },
    late: { fillColor: "#EFF6FF", textColor: "#1E40AF", borderColor: "#DBEAFE" },
    absent: { fillColor: "#FEF2F2", textColor: "#991B1B", borderColor: "#FEE2E2" },
    holiday: { fillColor: "#FFFBEB", textColor: "#92400E", borderColor: "#FEF3C7" },
    "half-day": { fillColor: "#FFFBEB", textColor: "#92400E", borderColor: "#FEF3C7" },
    leave: { fillColor: "#FAF5FF", textColor: "#6B21A8", borderColor: "#F3E8FF" },
    "week-off": { fillColor: "#F8FAFC", textColor: "#475569", borderColor: "#F1F5F9" },
    default: { fillColor: "#FFFFFF", textColor: "#64748B", borderColor: "#E2E8F0" },
  };

  const key = (status || "").toLowerCase();
  if (key.includes("leave")) return styles.leave;
  if (key.includes("weekoff") || key.includes("week-off")) return styles["week-off"];
  if (key.includes("half")) return styles["half-day"];

  return styles[key] || styles.default;
};

export const buildCalendarPdfContent = ({
  selectedMonth,
  calenderData = [],
  holidays = [],
  leaves = [],
  weekOffs = [],
}) => {
  const employee = calenderData?.[0] || {};
  const employeeCalendarData = employee.attendance || [];
  const dayHeaders = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [year, month] = selectedMonth.split("-").map(Number);
  const monthIndex = month - 1;
  const monthTitle = new Date(year, monthIndex).toLocaleString("default", { month: "long", year: "numeric" });

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const firstDayOfWeek = (new Date(year, monthIndex, 1).getDay() + 6) % 7;

  const body = [];

  // Header Row: Minimalist and Clean
  body.push(
    dayHeaders.map((label) => ({
      text: label.toUpperCase(),
      fontSize: 7,
      bold: true,
      alignment: "center",
      fillColor: "#F8FAFC",
      color: "#64748B",
      margin: [0, 8, 0, 8],
      border: [false, false, false, true],
      borderColor: "#E2E8F0"
    }))
  );

  const cells = [];
  // Empty start cells
  for (let i = 0; i < firstDayOfWeek; i++) {
    cells.push({ text: "", border: [false, false, false, false], fillColor: "#FFFFFF" });
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const findDate = (arr) => arr.find(d => {
      const dt = new Date(d.date);
      return dt.getUTCDate() === day && dt.getUTCMonth() === monthIndex;
    });

    const dayData = findDate(employeeCalendarData);
    const holiday = findDate(holidays);
    const leave = findDate(leaves);
    const weekOff = findDate(weekOffs);

    const displayData = dayData || (holiday ? { status: "holiday" } : leave ? { status: leave.type === "half-day" ? "half-day" : "leave" } : weekOff ? { status: "week-off" } : null);
    const style = getPdfCalendarCellStyle(displayData?.status);

    cells.push({
      margin: [5, 5, 5, 5],
      stack: [
        { text: String(day), fontSize: 8, bold: true, color: "#94A3B8", alignment: "right", margin: [0, 0, 2, 0] },
        {
          text: displayData ? buildAttendanceDisplayText(displayData) : "",
          fontSize: 7,
          bold: true,
          color: style.textColor,
          alignment: "center",
          margin: [0, 6, 0, 6]
        },
        displayData?.punch_in ? {
          table: {
            widths: ['*', '*'],
            body: [
              [
                {
                  stack: [
                    { text: 'IN', fontSize: 4, color: '#94A3B8', bold: true },
                    { text: formatPdfTime(displayData.punch_in), fontSize: 6, color: '#475569', bold: true }
                  ],
                  alignment: 'center'
                },
                {
                  stack: [
                    { text: 'OUT', fontSize: 4, color: '#94A3B8', bold: true },
                    { text: formatPdfTime(displayData.punch_out), fontSize: 6, color: '#475569', bold: true }
                  ],
                  alignment: 'center'
                }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [2, 6, 2, 0]
        } : { text: "", margin: [0, 14, 0, 0] }
      ],
      fillColor: style.fillColor,
      border: [true, true, true, true],
      borderColor: [style.borderColor, style.borderColor, style.borderColor, style.borderColor],
    });
  }

  // Padding end cells
  while (cells.length % 7 !== 0) {
    cells.push({ text: "", border: [false, false, false, false] });
  }

  for (let i = 0; i < cells.length; i += 7) {
    body.push(cells.slice(i, i + 7));
  }

  return [
    // Modern Header Layout with Swapped Positions
    {
      columns: [
        {
          width: 'auto',
          stack: [
            { text: "ATTENDANCE REPORT", fontSize: 8, color: "#94A3B8", bold: true, characterSpacing: 1 },
            { text: monthTitle, fontSize: 18, bold: true, color: "#1E293B", margin: [0, 2, 0, 8] },
            {
              text: "OFFICIAL RECORD",
              fontSize: 7,
              color: "#64748B",
              alignment: "left",
              margin: [0, 0, 0, 0],
              bold: true,
              background: '#F1F5F9'
            }
          ]
        },
        {
          width: '*',
          stack: [
            { text: employee.name || "--", fontSize: 14, bold: true, color: "#1E293B", alignment: "right" },
            { text: employee.designation || "--", fontSize: 10, color: "#64748B", alignment: "right", margin: [0, 2, 0, 12] },
            {
              columns: [
                {
                  width: '*',
                  text: ''
                },
                {
                  width: 'auto',
                  stack: [
                    { text: "DEPARTMENT", fontSize: 7, color: "#94A3B8", bold: true, alignment: "right" },
                    { text: employee.department || "--", fontSize: 9, color: "#475569", bold: true, alignment: "right" }
                  ],
                  margin: [0, 0, 20, 0]
                },
                {
                  width: 'auto',
                  stack: [
                    { text: "EMPLOYEE ID", fontSize: 7, color: "#94A3B8", bold: true, alignment: "right" },
                    { text: employee.employee_code || employee.employee_id || "--", fontSize: 9, color: "#475569", bold: true, alignment: "right" }
                  ]
                }
              ]
            }
          ]
        }
      ],
      margin: [0, 0, 0, 30]
    },
    // Main Calendar Table
    {
      table: {
        headerRows: 1,
        widths: ["*", "*", "*", "*", "*", "*", "*"],
        body,
        heights: (rowIndex) => (rowIndex === 0 ? 20 : 60),
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => "#E2E8F0",
        vLineColor: () => "#E2E8F0",
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 8,
        paddingBottom: () => 8,
      }
    },
    // Legend Footer
    {
      margin: [0, 24, 0, 0],
      columns: [
        {
          text: "Generated on " + new Date().toLocaleDateString(),
          fontSize: 7,
          color: "#94A3B8"
        },
        {
          alignment: "right",
          columns: [
            {
              width: "auto",
              columns: [
                {
                  canvas: [
                    { type: "ellipse", x: 4, y: 4, r1: 3, r2: 3, color: "#22C55E" }
                  ]
                },
                { text: " Present  ", fontSize: 7, color: "#64748B", margin: [4, 0, 5, 0] }
              ]
            },
            {
              width: "auto",
              columns: [
                {
                  canvas: [
                    { type: "ellipse", x: 4, y: 4, r1: 3, r2: 3, color: "#3B82F6" }
                  ]
                },
                { text: " Late  ", fontSize: 7, color: "#64748B", margin: [4, 0, 5, 0] }
              ]
            },
            {
              width: "auto",
              columns: [
                {
                  canvas: [
                    { type: "ellipse", x: 4, y: 4, r1: 3, r2: 3, color: "#EF4444" }
                  ]
                },
                { text: " Absent  ", fontSize: 7, color: "#64748B", margin: [4, 0, 5, 0] }
              ]
            },
            {
              width: "auto",
              columns: [
                {
                  canvas: [
                    { type: "ellipse", x: 4, y: 4, r1: 3, r2: 3, color: "#F59E0B" }
                  ]
                },
                { text: " Holiday", fontSize: 7, color: "#64748B", margin: [4, 0, 0, 0] }
              ]
            }
          ]
        }
      ]
    }
  ];
};
